import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { queryKeys } from '~/api/queryKeys';
import { extractApiError } from '~/utils/extract-api-error';

import {
  decideCost,
  getCostEstimateHistory,
  getPendingCostEstimates,
  getRunCostEstimate,
  getRunStageErrors,
  retryCostEstimate,
} from '../services/run-ledger.service';
import type { CostDecisionRequest } from '../types/run-ledger.api.types';

/** §93 — the run's stage-error ledger. */
export function useRunStageErrors(runRef: string) {
  return useQuery({
    queryKey: queryKeys.catalogService.runStageErrors(runRef),
    queryFn: () => getRunStageErrors(runRef),
    enabled: !!runRef,
  });
}

/** §95 — the run's latest cost estimate; read only while the gate holds the run. */
export function useRunCostEstimate(runRef: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.catalogService.runCostEstimate(runRef),
    queryFn: () => getRunCostEstimate(runRef),
    enabled: !!runRef && enabled,
  });
}

/** §95 — estimates waiting for a decision in this subsidiary. */
export function usePendingCostEstimates() {
  return useQuery({
    queryKey: queryKeys.catalogService.costEstimates.pending(),
    queryFn: getPendingCostEstimates,
  });
}

/** §95 — approve / reject; the run, its progress and the pending list all change. */
export function useCostDecision(runRef: string) {
  const { t } = useTranslation('catalog-enrichment-run');
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: CostDecisionRequest) => decideCost(runRef, request),
    onSuccess: async (estimate) => {
      toast.success(
        estimate.status === 'APPROVED'
          ? t('costGate.toast.approved')
          : t('costGate.toast.rejected'),
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.catalogService.run(runRef) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.catalogService.costEstimates.all() }),
      ]);
    },
    onError: (error) => {
      toast.error(t('costGate.toast.failed'), {
        description: extractApiError(error, t('costGate.toast.genericError')),
      });
    },
  });
}

/**
 * §96 — re-ask the estimate of a run held because it could not be priced. Priced under the
 * thresholds the run starts; over one it stays held with figures; failed again it stays held.
 */
export function useCostEstimateRetry(runRef: string) {
  const { t } = useTranslation('catalog-enrichment-run');
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => retryCostEstimate(runRef),
    onSuccess: async (estimate) => {
      if (estimate.error) {
        toast.warning(t('costGate.retry.stillFailing'), { description: estimate.error });
      } else if (estimate.status === 'PENDING_APPROVAL') {
        toast.info(t('costGate.retry.pricedHeld'));
      } else {
        toast.success(t('costGate.retry.released'));
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.catalogService.run(runRef) }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.catalogService.runCostEstimate(runRef),
        }),
        queryClient.invalidateQueries({ queryKey: queryKeys.catalogService.costEstimates.all() }),
      ]);
    },
    onError: (error) => {
      toast.error(t('costGate.retry.failed'), {
        description: extractApiError(error, t('costGate.toast.genericError')),
      });
    },
  });
}

/** §95 — one page of the decision history (`status` unset = every decided status). */
export function useCostEstimateHistory(params: {
  status?: string | undefined;
  page: number;
  size: number;
}) {
  return useQuery({
    queryKey: queryKeys.catalogService.costEstimates.history({ ...params }),
    queryFn: () => getCostEstimateHistory(params),
    placeholderData: keepPreviousData,
  });
}
